/**
 * Localization of the port. The game's own language is Russian (that is what the original SWF and the decompiled
 * code contain), so every user-visible Russian string is the translation key: the catalogue maps it to the text of
 * the active language. Nothing in the game code changes - the runtime shows translated text through
 * `Runtime.localize` (see installLocalizer), so game logic keeps comparing and saving the canonical strings.
 *
 * Three steps per string, with the result cached:
 *  1. the whole string is in the catalogue (all static texts and most messages);
 *  2. otherwise it is a string the game composed from fragments ("Ход #" + n + "... Ходит: " + name), so every
 *     catalogue fragment inside it is substituted;
 *  3. otherwise it is left alone - player names, numbers, and anything not yet translated, which is counted so a
 *     test can require a complete translation.
 *
 * The language is chosen before the game is constructed (the data tables build weapon names and descriptions on
 * construction), which is why switching it restarts the game.
 *
 * On a touch screen (setTouchTexts) the result then gets the touch wording of the help texts that name keys and mouse
 * gestures (src/game/runtime/touch-variants.ts), also in the original language.
 */
import { flashRuntime } from '@flash';
import { touchVariant } from './touch-variants';

export type Language = 'uk' | 'en' | 'ru';

/** Languages offered in the settings screen; 'ru' shows the original text of the game (no catalogue). */
export const LANGUAGES: { id: Language; label: string }[] = [
  { id: 'uk', label: 'Українська' },
  { id: 'en', label: 'English' },
  { id: 'ru', label: 'Русский' },
];

/** Whether `value` names one of the offered languages. */
export function isLanguage(value: unknown): value is Language {
  return LANGUAGES.some((l) => l.id === value);
}

/**
 * The language of a first start, from the system's (the browser's or the phone's) preferred language, its first entry:
 * Ukrainian for Ukrainian, Russian for Russian, English for English and every other language.
 */
export function systemLanguage(preferred: readonly string[]): Language {
  const primary = String(preferred.find((tag) => typeof tag === 'string' && tag.trim() !== '') ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return primary === 'uk' ? 'uk' : primary === 'ru' ? 'ru' : 'en';
}

/** Letters that may not touch a short fragment ("АТ" must not match inside a word). */
const WORD = 'А-Яа-яЁёЇїІіЄєҐґA-Za-z';

/**
 * Counted nouns: the game appends the Russian ending to a stem ("3 выстрел" + "а"), so the composed phrase has no
 * catalogue entry. A rule turns the whole "<number> <stem><ending>" into the right form of the target language
 * (Ukrainian: one / few / many, English: one / other). Rules live in the catalogue under "$plural".
 */
export interface PluralRule {
  /** Russian stem as it appears in the code, without the number and without the ending (e.g. " выстрел"). */
  stem: string;
  /** Endings the game can append (including ""), longest first is not required. */
  endings: string[];
  /** Target forms: [one, few, many] for Ukrainian, [one, other] for English. */
  forms: string[];
}

let current: Language = 'ru';
/** Touch wording of the help texts (setTouchTexts): the client is in touch mode. */
let touchTexts = false;
let catalogue: Record<string, string> = {};
let keysByChar: Map<string, string[]> | null = null;
let translated: RegExp | null = null;
let translatedShort: RegExp | null = null;
let plural: { re: RegExp; forms: string[] }[] = [];
const cache = new Map<string, string>();
/** Strings the catalogue covers, including those whose translation is the same word ("Арсенал", "АТ"). */
const handled = new Set<string>();
const missing = new Map<string, number>();

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const isWord = (ch: string) => !!ch && new RegExp(`[${WORD}]`).test(ch);

/** Installs the catalogue of `lang` ('ru' = the original text, no catalogue). */
export function setLanguage(lang: Language, strings: Record<string, string> = {}): void {
  current = lang;
  // The page's language too: the DOM overlays (dialogs, the chat's field) are read out and hyphenated in it.
  if (typeof document !== 'undefined' && document.documentElement) document.documentElement.lang = lang;
  const { $plural, ...rest } = strings as Record<string, any>;
  catalogue = lang === 'ru' ? {} : (rest as Record<string, string>);
  plural =
    lang === 'ru' || !Array.isArray($plural)
      ? []
      : ($plural as PluralRule[]).map((r) => {
          const endings = r.endings.filter(Boolean).map(escapeRe).join('|');
          // Sticky: tried at one position by the scanner. The number and the noun can be separated by markup
          // ("стоит <b>3</b> очка навыков"). A rule without an empty ending must see one, so the abbreviated
          // forms the catalogue covers ("3 очк.") are left to it.
          const optional = r.endings.includes('') ? '?' : '';
          return { re: new RegExp(`(\\d+)((?:<[^>]+>)*)${escapeRe(r.stem)}(?:${endings})${optional}(?![${WORD}])`, 'y'), forms: r.forms };
        });
  keysByChar = null;
  translated = null;
  translatedShort = null;
  cache.clear();
  handled.clear();
  missing.clear();
}

export function language(): Language {
  return current;
}

/**
 * Touch mode of the texts (the client's touch mode: a phone, the app, a touch laptop after its first real touch): the
 * help texts that name keys and mouse gestures describe the on-screen controls instead (src/game/runtime/
 * touch-variants.ts), in every language including the original. Text already on screen keeps its wording; everything
 * shown from now on gets the other one.
 */
export function setTouchTexts(on: boolean): void {
  if (on === touchTexts) return;
  touchTexts = on;
  cache.clear();
}

export function touchTextsOn(): boolean {
  return touchTexts;
}

/**
 * Catalogue keys grouped by their first character, longest first, plus the regexes that recognize text which is
 * already a translation (the game re-assigns whole HTML paragraphs it read back from a field).
 */
function buildMatchers(): void {
  if (keysByChar) return;
  keysByChar = new Map();
  for (const key of Object.keys(catalogue).sort((a, b) => b.length - a.length)) {
    if (!key) continue;
    const list = keysByChar.get(key[0]);
    if (list) list.push(key);
    else keysByChar.set(key[0], [key]);
  }
  const values = [...new Set(Object.values(catalogue))].sort((a, b) => b.length - a.length);
  const longValues = values.filter((v) => v.length >= 8);
  const shortValues = values.filter((v) => v.length > 1 && v.length < 8 && /[А-Яа-яЁёЇїІіЄєҐґ]/.test(v));
  translated = longValues.length ? new RegExp(longValues.map(escapeRe).join('|')) : null;
  translatedShort = shortValues.length ? new RegExp(`(?<![${WORD}])(?:${shortValues.map(escapeRe).join('|')})(?![${WORD}])`) : null;
}

/** Picks the form of a counted noun: Ukrainian has one / few / many, English one / other. */
function pluralForm(forms: string[], n: number): string {
  if (forms.length < 3) return forms[n === 1 ? 0 : forms.length - 1];
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

/**
 * One left-to-right pass over the text, taking the longest match at every position: a catalogue key (a whole
 * sentence beats the fragment inside it) or a counted-noun rule. Replacements are emitted, never rescanned, so a
 * translated word can never be matched again by a shorter key. A key that starts or ends with a letter needs a
 * non-letter next to it, so " выстрел" matches "5 выстрел" but not inside "выстреливает".
 */
function substitute(text: string): { out: string; matched: boolean } {
  buildMatchers();
  let out = '';
  let matched = false;
  for (let i = 0; i < text.length; ) {
    let bestLen = 0;
    let bestText: string | null = null;
    for (const key of keysByChar!.get(text[i]) ?? []) {
      if (key.length <= bestLen || !text.startsWith(key, i)) continue;
      if (isWord(key[0]) && isWord(text[i - 1])) continue;
      if (isWord(key[key.length - 1]) && isWord(text[i + key.length])) continue;
      bestLen = key.length;
      bestText = catalogue[key];
      break; // the candidates of this character are sorted longest first
    }
    for (const rule of plural) {
      rule.re.lastIndex = i;
      const m = rule.re.exec(text);
      if (!m || m[0].length <= bestLen) continue;
      bestLen = m[0].length;
      bestText = `${m[1]}${m[2]} ${pluralForm(rule.forms, Number(m[1]))}`;
    }
    if (bestText !== null && bestLen > 0) {
      out += bestText;
      i += bestLen;
      matched = true;
    } else {
      out += text[i];
      i++;
    }
  }
  return { out, matched };
}

/** Translates a string for display; unknown text is returned unchanged and counted as missing. */
export function translate(text: string): string {
  if (!text) return text;
  if (current === 'ru') {
    if (!touchTexts) return text;
    let done = cache.get(text);
    if (done === undefined) cache.set(text, (done = touchVariant('ru', text)));
    return done;
  }
  if (LANGUAGES.some((l) => l.label === text)) return text; // the names of the languages stay in their own language
  const done = cache.get(text);
  if (done !== undefined) {
    if (!handled.has(text) && /[А-Яа-яЁё]/.test(text)) missing.set(text, (missing.get(text) ?? 0) + 1);
    return done;
  }
  let out = catalogue[text];
  if (out !== undefined) handled.add(text);
  else {
    const sub = substitute(text);
    if (sub.matched) {
      out = sub.out;
      handled.add(text);
    } else {
      out = text;
      buildMatchers();
      // Text the game read back from a field it had already filled is a translation, not a missing one.
      if ((translated && translated.test(text)) || (translatedShort && translatedShort.test(text))) handled.add(text);
      else if (/[А-Яа-яЁё]/.test(text)) missing.set(text, (missing.get(text) ?? 0) + 1);
    }
  }
  if (touchTexts) out = touchVariant(current, out);
  cache.set(text, out);
  return out;
}

/**
 * A player's name for display: a guest's default name ("Игрок-7K2Q", handed out by auth.guest) is game text and gets
 * the prefix of the active language; any other name is the player's own and stays as typed.
 */
export function playerName(name: string): string {
  return /^Игрок-[0-9A-Za-z]{4,6}$/.test(name) ? translate(name) : name;
}

/** Source strings shown without a translation in the active language, most frequent first. */
export function missingTranslations(): { text: string; count: number }[] {
  return [...missing].map(([text, count]) => ({ text, count })).sort((a, b) => b.count - a.count);
}

export function catalogueSize(): number {
  return Object.keys(catalogue).length;
}

/**
 * Loads the catalogue of `lang` through the platform (public/i18n/<lang>.json is shipped with the assets) and makes
 * the Flash runtime show translated text. Called by the host before the game is constructed.
 */
export async function initLanguage(platform: { readText(url: string): Promise<string> }, lang: Language, baseUrl = 'i18n'): Promise<number> {
  let strings: Record<string, string> = {};
  if (lang !== 'ru') {
    try {
      strings = JSON.parse(await platform.readText(`${baseUrl}/${lang}.json`)) as Record<string, string>;
    } catch {
      strings = {}; // a missing catalogue falls back to the original text instead of breaking the game
    }
  }
  setLanguage(lang, strings);
  flashRuntime().localize = translate;
  // Art with lettering drawn into it has per-language variants in the asset bundles (manifest `localized`).
  flashRuntime().assetVariant = lang === 'ru' ? null : lang;
  return catalogueSize();
}
